import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCorePermissions, useGenericManager } from "spark-core-dx/hooks";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { ItemServiceFactory } from "../../services/ItemServiceFactory";
import { useDashboard } from "./hooks/useDashboard";

export const DashboardDomainContext = createContext();

const DASHBOARD_BASE_ROUTE = "/dashboard";
const DASHBOARD_MODEL_ROUTE = "/dashboard/model";
const MODEL_WORKSPACES_BASE_ROUTE = "/model-workspaces";

const normalizeIconOptions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => {
      const value = item?.Value ?? item?.value ?? item?.Key ?? item?.Name ?? item?._id;
      const text = item?.Text ?? item?.text ?? item?.DisplayName ?? item?.Name ?? item?.Key ?? value;

      if (!value || !text) {
        return null;
      }

      return {
        Text: String(text),
        Value: value,
        Icon: item?.Icon ?? value,
        Disabled: item?.Disabled === true,
        Important: item?.Important === true,
        Sequence: typeof item?.Sequence === "number" ? item.Sequence : -1,
      };
    })
    .filter(Boolean)
    .sort((a, b) => String(a?.Text ?? "").localeCompare(String(b?.Text ?? "")));

const getDefaultPage = (pages = []) => {
  const activePages = pages.filter((page) => !page?.IsDeleted);
  return (
    activePages.find((page) => page?.IsDefault) ??
    activePages.sort((a, b) => (a?.SortOrder ?? 0) - (b?.SortOrder ?? 0))[0] ??
    null
  );
};

export function DashboardDomainContextProvider({ children }) {
  const [mode, setMode] = useState("view");
  const [activeContextMenu, setActiveContextMenu] = useState(null);
  const [isFilterFlyoutOpen, setIsFilterFlyoutOpen] = useState(false);
  const [isFilterDebouncing, setIsFilterDebouncing] = useState(false);
  const [dataModelCount, setDataModelCount] = useState(null);
  const [isDataModelsLoading, setIsDataModelsLoading] = useState(false);
  const [hasLoadedInitialDataModelCount, setHasLoadedInitialDataModelCount] = useState(false);
  const [visualRefreshCycleId, setVisualRefreshCycleId] = useState(0);
  const [isRefreshingVisuals, setIsRefreshingVisuals] = useState(false);

  const { dashboardId, pageId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const { canCreate, canDelete, canEdit, canHardDelete } = useCorePermissions();

  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const iconMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Icon);
  const modelMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DataModel);
  const modelMgrRef = useRef(modelMgr);
  const activeVisualRefreshCycleIdRef = useRef(0);
  const visualRefreshPendingIdsRef = useRef(new Set());
  const rawIconSelectData = iconMgr?.selectData?.find((x) => x.FieldName === "Icon")?.jsonData;
  const iconSelectData = useMemo(() => {
    const normalizedSelectData = normalizeIconOptions(rawIconSelectData);
    if (normalizedSelectData.length > 0) {
      return normalizedSelectData;
    }

    const coreIcons = coreContext?.Icons;
    if (!coreIcons || typeof coreIcons !== "object") {
      return [];
    }

    return Object.entries(coreIcons)
      .map(([key, icon]) => ({
        Text: icon?.DisplayName ?? icon?.Name ?? key,
        Value: key,
        Icon: key,
        Disabled: icon?.Disabled === true,
        Important: icon?.Important === true,
        Sequence: typeof icon?.Sequence === "number" ? icon.Sequence : -1,
      }))
      .sort((a, b) => String(a?.Text ?? "").localeCompare(String(b?.Text ?? "")));
  }, [coreContext?.Icons, rawIconSelectData]);

  const dashboardLib = useDashboard(dashboardId, pageId);
  const activeDashboard = dashboardLib.activeDashboard;
  const activeDraftWorkspaceSummary = dashboardLib.activeDraftWorkspaceSummary ?? null;
  const activePublishedWorkspaceSummary = dashboardLib.activePublishedWorkspaceSummary ?? null;
  const isDashboardDataLoading = dashboardLib.isLoading;
  const isViewMode = mode === "view";
  const normalizedPath = String(location?.pathname ?? "").toLowerCase();
  const isModelDesigner = normalizedPath.includes(`${DASHBOARD_MODEL_ROUTE}/`);
  const isVisualDesigner = !isModelDesigner;

  const profileId = coreContext?.Profile?._id;
  const createdById = activeDashboard?.CreatedById;
  const isOwner = (createdById != null && profileId != null && createdById === profileId) || canEdit("ManageAllProfiles");

  const currentDetails = isViewMode
    ? (activeDashboard?.PublishedDetails ?? activeDashboard?.DraftDetails)
    : activeDashboard?.DraftDetails;
  const allPages = useMemo(() => currentDetails?.Pages ?? [], [currentDetails]);
  const visiblePages = useMemo(
    () => (isViewMode ? allPages.filter((page) => !page?.IsDeleted) : allPages),
    [allPages, isViewMode]
  );

  const selectedPageData = useMemo(() => {
    if (!pageId) {
      return null;
    }

    const pageSet = isViewMode ? visiblePages : allPages;
    return pageSet.find((page) => page?.Key === pageId) ?? null;
  }, [allPages, visiblePages, pageId, isViewMode]);

  const fallbackPage = useMemo(() => getDefaultPage(allPages), [allPages]);
  const activePage = selectedPageData ?? fallbackPage;
  const hasPages = allPages.some((page) => !page?.IsDeleted);
  const selectedPageVisuals = useMemo(
    () => (Array.isArray(activePage?.Visuals) ? activePage.Visuals.filter((visual) => visual?.IsActive !== false) : []),
    [activePage]
  );

  const inactivePageVisuals = useMemo(
    () => (Array.isArray(activePage?.Visuals) ? activePage.Visuals.filter((visual) => visual?.IsActive === false) : []),
    [activePage]
  );
  const isDashboardLoading =
    isDashboardDataLoading ||
    (!!dashboardId && !hasLoadedInitialDataModelCount);

  useEffect(() => {
    setDataModelCount(null);
    setHasLoadedInitialDataModelCount(!dashboardId);
    setIsDataModelsLoading(false);
    activeVisualRefreshCycleIdRef.current = 0;
    visualRefreshPendingIdsRef.current = new Set();
    setVisualRefreshCycleId(0);
    setIsRefreshingVisuals(false);
  }, [dashboardId]);

  useEffect(() => {
    activeVisualRefreshCycleIdRef.current = 0;
    visualRefreshPendingIdsRef.current = new Set();
    setVisualRefreshCycleId(0);
    setIsRefreshingVisuals(false);
  }, [activePage?.Key]);

  useEffect(() => {
    if (!coreState?.get("SelectData")?.find((x) => x.DataOperationItem === "Icon")) {
      iconMgr.GetSelectData({
        DisplayName: "Icon",
        DataOperationItem: "Icon",
        FieldName: "Icon",
      });
    }
  }, [coreState, iconMgr]);

  useEffect(() => {
    modelMgrRef.current = modelMgr;
  }, [modelMgr]);

  const refreshDataModelCount = useCallback(async () => {
    if (!dashboardId) {
      setDataModelCount(null);
      setIsDataModelsLoading(false);
      setHasLoadedInitialDataModelCount(true);
      return 0;
    }

    if (isDashboardDataLoading || !activeDashboard?._id) {
      setDataModelCount(null);
      setIsDataModelsLoading(false);
      return 0;
    }

    setIsDataModelsLoading(true);
    try {
      const result = await modelMgrRef.current?.Query({
        model: "x => x.IsActive == true && x.IsDeleted == false",
      });

      const workspaceId = String(activeDashboard?.DataModelWorkspaceId ?? "").trim();
      const activeModels = Array.isArray(result?.Items) ? result.Items : [];
      const nextCount = workspaceId
        ? activeModels.filter((item) => String(item?.DataModelWorkspaceId ?? "").trim() === workspaceId).length
        : 0;

      setDataModelCount(nextCount);
      return nextCount;
    } finally {
      setIsDataModelsLoading(false);
      setHasLoadedInitialDataModelCount(true);
    }
  }, [activeDashboard?._id, activeDashboard?.DataModelWorkspaceId, dashboardId, isDashboardDataLoading]);

  const refreshAllVisuals = useCallback(() => {
    const visualIds = selectedPageVisuals
      .map((visual) => String(visual?.VisualId ?? visual?._id ?? visual?.UniqueId ?? "").trim())
      .filter(Boolean);

    if (visualIds.length === 0) {
      setIsRefreshingVisuals(false);
      return false;
    }

    const nextCycleId = activeVisualRefreshCycleIdRef.current + 1;
    activeVisualRefreshCycleIdRef.current = nextCycleId;
    visualRefreshPendingIdsRef.current = new Set(visualIds);
    setIsRefreshingVisuals(true);
    setVisualRefreshCycleId(nextCycleId);
    return true;
  }, [selectedPageVisuals]);

  const markVisualRefreshSettled = useCallback((visualId, cycleId) => {
    const normalizedVisualId = String(visualId ?? "").trim();
    if (!normalizedVisualId) {
      return;
    }

    if (!Number.isFinite(Number(cycleId)) || Number(cycleId) <= 0) {
      return;
    }

    if (Number(cycleId) !== activeVisualRefreshCycleIdRef.current) {
      return;
    }

    const pendingIds = visualRefreshPendingIdsRef.current;
    if (!pendingIds.has(normalizedVisualId)) {
      return;
    }

    pendingIds.delete(normalizedVisualId);
    if (pendingIds.size === 0) {
      setIsRefreshingVisuals(false);
    }
  }, []);

  useEffect(() => {
    void refreshDataModelCount();
  }, [refreshDataModelCount]);

  useEffect(() => {
    if (!dashboardId || !activeDashboard || isDashboardLoading) {
      return;
    }

    if (dataModelCount === 0) {
      if (!isModelDesigner) {
        navigate(`${DASHBOARD_MODEL_ROUTE}/${dashboardId}`, { replace: true });
      }
      return;
    }

    if (isModelDesigner) {
      return;
    }

    if (!activePage) {
      return;
    }

    if (pageId !== activePage.Key) {
      navigate(`${DASHBOARD_BASE_ROUTE}/${dashboardId}/page/${activePage.Key}`, { replace: true });
    }
  }, [activeDashboard, activePage, dashboardId, dataModelCount, hasPages, isDashboardLoading, isModelDesigner, navigate, pageId]);

  const navigateToDashboardList = () => navigate(DASHBOARD_BASE_ROUTE);

  const resolveDashboardRoute = useCallback((targetDashboardId, nextPageKey = null) => {
    if (!targetDashboardId) {
      return DASHBOARD_BASE_ROUTE;
    }

    const targetDashboard = (dashboardLib.dashboardList ?? []).find(
      (dashboard) => String(dashboard?._id) === String(targetDashboardId)
    ) ?? null;
    const targetPages = targetDashboard?.DraftDetails?.Pages ?? targetDashboard?.PublishedDetails?.Pages ?? [];
    const activePages = targetPages.filter((page) => !page?.IsDeleted);
    const explicitPage = nextPageKey
      ? activePages.find((page) => String(page?.Key ?? "") === String(nextPageKey))
      : null;
    const defaultPage = explicitPage ?? getDefaultPage(activePages);

    if (defaultPage?.Key) {
      return `${DASHBOARD_BASE_ROUTE}/${targetDashboardId}/page/${defaultPage.Key}`;
    }

    return `${DASHBOARD_BASE_ROUTE}/${targetDashboardId}`;
  }, [dashboardLib.dashboardList]);

  const navigateToDashboard = (nextDashboardId, nextPageKey = null) => {
    if (!nextDashboardId) {
      navigateToDashboardList();
      return;
    }

    if (nextPageKey) {
      navigate(`${DASHBOARD_BASE_ROUTE}/${nextDashboardId}/page/${nextPageKey}`);
      return;
    }

    navigate(`${DASHBOARD_BASE_ROUTE}/${nextDashboardId}`);
  };

  const navigateToModelDesigner = (targetDashboardId = dashboardId) => {
    if (targetDashboardId) {
      navigate(`${DASHBOARD_MODEL_ROUTE}/${targetDashboardId}`);
      return;
    }

    navigate(MODEL_WORKSPACES_BASE_ROUTE);
  };

  const navigateToVisualDesigner = (nextDashboardId = dashboardId, nextPageKey = activePage?.Key ?? null) => {
    if (!nextDashboardId) {
      navigateToDashboardList();
      return;
    }

    if (nextPageKey) {
      navigate(`${DASHBOARD_BASE_ROUTE}/${nextDashboardId}/page/${nextPageKey}`);
      return;
    }

    navigate(`${DASHBOARD_BASE_ROUTE}/${nextDashboardId}`);
  };

  const navigateToVisualDesignerFromIcon = (nextDashboardId = dashboardId, nextPageKey = activePage?.Key ?? null) => {
    if (!nextDashboardId) {
      navigateToDashboardList();
      return;
    }

    navigate(resolveDashboardRoute(nextDashboardId, nextPageKey));
  };

  const navigateToPage = (nextPageKey) => {
    if (!dashboardId || !nextPageKey) {
      return;
    }

    navigate(`${DASHBOARD_BASE_ROUTE}/${dashboardId}/page/${nextPageKey}`);
  };

  const switchMode = (nextMode) => {
    setMode(nextMode === "design" ? "design" : "view");
  };

  const createNewDashboard = async (dashboard) => {
    const response = await dashboardLib.createDashboard(dashboard);

    if (response?.Success) {
      switchMode("design");
      const createdDashboard = response?.Items?.first?.() ?? response?.Items?.[0] ?? null;
      navigateToModelDesigner(createdDashboard?._id);
    }

    return response ?? { Success: false };
  };

  const editDashboardById = async (targetDashboardId, dashboard) => {
    if (!targetDashboardId) {
      return { Success: false };
    }

    return dashboardLib.editDashboardById(targetDashboardId, dashboard) ?? { Success: false };
  };

  const deleteDashboard = async (deletePermanently = false) => {
    if (!activeDashboard?._id) {
      return false;
    }

    const result = await dashboardLib.deleteDashboard(activeDashboard._id, deletePermanently);
    if (result?.Success) {
      switchMode("view");
      navigateToDashboardList();
      return true;
    }

    return false;
  };

  const deleteDashboardById = async (targetDashboardId, deletePermanently = false) => {
    if (!targetDashboardId) {
      return false;
    }

    const result = await dashboardLib.deleteDashboard(targetDashboardId, deletePermanently);
    if (result?.Success && String(targetDashboardId) === String(activeDashboard?._id)) {
      switchMode("view");
      navigateToDashboardList();
    }

    return !!result?.Success;
  };

  const createPage = async (page) => {
    const result = await dashboardLib.createPage(page);
    if (result?.Success && result?.page?.Key) {
      navigateToPage(result.page.Key);
    }
    return result;
  };

  const clonePage = async (pageKey) => {
    const result = await dashboardLib.clonePage(pageKey);
    if (result?.Success && result?.page?.Key) {
      navigateToPage(result.page.Key);
    }
    return result;
  };

  const editPage = async (pageKey, pagePatch) => {
    const result = await dashboardLib.editPage(pageKey, pagePatch);
    if (result?.Success && pageKey === activePage?.Key && result?.page?.Key) {
      navigateToPage(result.page.Key);
    }
    return result;
  };

  const deletePage = async (pageKey, deletePermanently = false) => {
    const action = deletePermanently ? dashboardLib.hardDeletePage : dashboardLib.softDeletePage;
    const result = await action(pageKey);

    if (result?.Success && activePage?.Key === pageKey) {
      const refreshedPages = result.dashboard?.DraftDetails?.Pages ?? [];
      const nextPage = getDefaultPage(refreshedPages);
      if (nextPage?.Key) {
        navigateToPage(nextPage.Key);
      } else {
        navigate(`${DASHBOARD_BASE_ROUTE}/${dashboardId}`);
      }
    }

    return result;
  };

  const restorePage = async (pageKey) => {
    const result = await dashboardLib.restorePage(pageKey);
    if (result?.Success) {
      navigateToPage(pageKey);
    }
    return result;
  };

  const movePage = async (pageKey, direction) => dashboardLib.movePage(pageKey, direction);
  const reorderPage = async (pageKey, targetPageKey) => dashboardLib.reorderPage(pageKey, targetPageKey);
  const handleAssignActiveContextMenu = (event, newActiveContextMenu) => {
    event?.stopPropagation?.();
    setActiveContextMenu(newActiveContextMenu);
  };

  const getVisualFromCard = (card) => {
    if (!card?.i) {
      return null;
    }

    return selectedPageVisuals.find((visual) => String(visual?.VisualId) === String(card.i)) ?? null;
  };

  const columnCount = Number(activePage?.ColumnCount ?? 10);
  const hasDashboards = (dashboardLib.dashboardList?.length ?? 0) > 0;
  const hasModels = typeof dataModelCount === "number" && dataModelCount > 0;
  const isDashboardPublished = !!activeDashboard?.State?.IsPublished;
  const isCurrentPageEmpty = selectedPageVisuals.length === 0;
  const isTabPublished = isDashboardPublished;
  const isTabEmpty = isCurrentPageEmpty;

  return (
    <DashboardDomainContext.Provider
      value={{
        dashboardLib,
        iconSelectData,
        mode,
        isViewMode,
        isModelDesigner,
        isVisualDesigner,
        isDashboardLoading,
        hasDashboards,
        hasPages,
        hasModels,
        isDashboardPublished,
        isTabPublished,
        isCurrentPageEmpty,
        isTabEmpty,
        isOwner,
        isFilterFlyoutOpen,
        isFilterDebouncing,
        isDataModelsLoading,
        isRefreshingVisuals,
        visualRefreshCycleId,
        canCreateDashboard: canCreate("Dashboard"),
        canEditDashboard: canEdit("Dashboard"),
        canDeleteDashboard: canDelete("Dashboard"),
        canHardDeleteDashboard: canHardDelete("Dashboard"),
        activeDashboard,
        activeDraftWorkspaceSummary,
        activePublishedWorkspaceSummary,
        selectedTabData: activeDashboard,
        selectedDashboardId: dashboardId ?? null,
        selectedPageId: activePage?.Key ?? null,
        selectedPageData: activePage,
        selectedPageVisuals,
        inactivePageVisuals,
        currentDetails,
        pages: allPages,
        visiblePages,
        columnCount,
        activeContextMenu,
        switchMode,
        navigateToDashboardList,
        navigateToDashboard,
        navigateToModelDesigner,
        navigateToVisualDesigner,
        navigateToVisualDesignerFromIcon,
        navigateToPage,
        createNewDashboard,
        editDashboardById,
        deleteDashboard,
        deleteDashboardById,
        createPage,
        clonePage,
        editPage,
        deletePage,
        restorePage,
        movePage,
        reorderPage,
        setIsFilterFlyoutOpen,
        setIsFilterDebouncing,
        refreshDataModelCount,
        refreshAllVisuals,
        markVisualRefreshSettled,
        handleAssignActiveContextMenu,
        getVisualFromCard,
        setActiveContextMenu,
      }}
    >
      {children}
    </DashboardDomainContext.Provider>
  );
}

export const useDashboardDomainContext = () => useContext(DashboardDomainContext);
